import { useEffect, useState, useMemo } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import ProductCard from '@/components/feature/ProductCard';
import { openProductQuoteOnWhatsApp } from '@/utils/whatsapp';
import {
  fetchAtas,
  fetchProdutoCategorias,
  fetchProdutos,
  type Ata,
  type Produto,
  type ProdutoCategoria,
} from '@/services/catalog';

interface QuoteVariant {
  id: number;
  cor: string;
  tamanho: string;
  sku: string;
  estoque: number;
  ativo: boolean;
}

export default function ProdutosPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [products, setProducts] = useState<Produto[]>([]);
  const [categories, setCategories] = useState<ProdutoCategoria[]>([]);
  const [atas, setAtas] = useState<Ata[]>([]);

  useEffect(() => {
    Promise.all([fetchProdutos(), fetchProdutoCategorias(), fetchAtas()]).then(([productsData, categoriesData, atasData]) => {
      setProducts(productsData);
      setCategories(categoriesData);
      setAtas(atasData);
    });
  }, []);

  const handleQuote = (
    product: Produto,
    variant: QuoteVariant | undefined,
    quantity: number,
  ) => {
    openProductQuoteOnWhatsApp({
      productName: product.nome,
      productSku: product.sku,
      brand: product.marca,
      variant,
      quantity,
      atasNomes: getAtasNomes(product.atasIds),
    });
  };

  const filteredProducts = useMemo(() => {
    let result = products.filter((p) => p.ativo);

    if (selectedCategory !== null) {
      result = result.filter((p) => p.categorias.includes(selectedCategory));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.nome.toLowerCase().includes(q) ||
          p.sku.toLowerCase().includes(q) ||
          p.marca.toLowerCase().includes(q) ||
          p.descricao.toLowerCase().includes(q),
      );
    }

    return result;
  }, [products, searchQuery, selectedCategory]);

  const getCategoryName = (catId: number) => {
    return categories.find((c) => c.id === catId)?.nome || '';
  };

  const getAtasNomes = (atasIds: number[]) => {
    return atasIds
      .map((id) => atas.find((a) => a.id === id))
      .filter(Boolean)
      .map((a) => a!.numero);
  };

  return (
    <main className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-16 bg-donnos-dark overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-donnos-green/20 via-transparent to-transparent"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-donnos-green/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-donnos-green/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4"></div>
        <div className="relative w-full px-4 sm:px-6 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block text-xs font-semibold text-donnos-green uppercase tracking-wider bg-donnos-green/10 px-3 py-1 rounded-full mb-4">
              Catálogo de Produtos
            </span>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Produtos para o Setor Público
            </h1>
            <p className="text-sm sm:text-base text-gray-400 max-w-2xl mx-auto mb-8">
              Equipamentos de TI, periféricos e suprimentos disponíveis via atas de registro de preços. Selecione a variante, a quantidade e solicite seu orçamento.
            </p>

            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por nome, marca ou SKU..."
                className="w-full bg-white/10 border border-white/20 rounded-xl pl-12 pr-4 py-3.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-donnos-green focus:bg-white/15 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden">
            <button
              onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg px-4 py-2.5 hover:border-donnos-green transition-colors"
            >
              <i className="ri-filter-3-line"></i>
              Filtros
              {selectedCategory !== null && (
                <span className="w-5 h-5 bg-donnos-green text-white text-xs rounded-full flex items-center justify-center">1</span>
              )}
            </button>
          </div>

          {/* Sidebar */}
          <aside className={`lg:w-64 flex-shrink-0 ${mobileFilterOpen ? 'block' : 'hidden'} lg:block`}>
            <div className="bg-white rounded-xl border border-gray-100 p-5 sticky top-24">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-gray-900">Categorias</h3>
                {selectedCategory !== null && (
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="text-xs text-donnos-green hover:text-donnos-green-dark font-medium"
                  >
                    Limpar
                  </button>
                )}
              </div>
              <ul className="space-y-1">
                <li>
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedCategory === null
                        ? 'bg-donnos-green/10 text-donnos-green font-semibold'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Todas as categorias
                    <span className="float-right text-xs text-gray-400 mt-0.5">
                      {products.filter((p) => p.ativo).length}
                    </span>
                  </button>
                </li>
                {categories.map((cat) => {
                  const count = products.filter(
                    (p) => p.ativo && p.categorias.includes(cat.id),
                  ).length;
                  if (count === 0) return null;
                  return (
                    <li key={cat.id}>
                      <button
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          selectedCategory === cat.id
                            ? 'bg-donnos-green/10 text-donnos-green font-semibold'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {cat.nome}
                        <span className="float-right text-xs text-gray-400 mt-0.5">{count}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Results Info */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
              <p className="text-sm text-gray-500">
                {filteredProducts.length === 0
                  ? 'Nenhum produto encontrado'
                  : `${filteredProducts.length} produto${filteredProducts.length !== 1 ? 's' : ''} encontrado${filteredProducts.length !== 1 ? 's' : ''}`}
                {selectedCategory !== null && (
                  <>
                    {' '}
                    <span className="text-gray-400">em</span>{' '}
                    <span className="text-donnos-green font-medium">{getCategoryName(selectedCategory)}</span>
                  </>
                )}
              </p>
              {searchQuery && (
                <p className="text-xs text-gray-400">
                  Resultados para "<span className="text-gray-600">{searchQuery}</span>"
                </p>
              )}
            </div>

            {/* Products Grid */}
            {filteredProducts.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <i className="ri-search-line text-2xl text-gray-400"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Nenhum produto encontrado</h3>
                <p className="text-sm text-gray-500 mb-4">Tente ajustar os filtros ou a busca.</p>
                <button
                  onClick={() => { setSearchQuery(''); setSelectedCategory(null); }}
                  className="text-sm text-donnos-green font-medium hover:underline"
                >
                  Limpar todos os filtros
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    categoryNames={product.categorias.map((c) => getCategoryName(c))}
                    atasNomes={getAtasNomes(product.atasIds)}
                    onQuote={(variant, quantity) => handleQuote(product, variant, quantity)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
